/**
 * ConnectData - the single, unified "new data source" experience.
 *
 * One flow, no "classic vs new" split:
 *   • Pick a source type (logo tiles) or just paste a connection string.
 *   • We test + introspect, then auto-map every table/column.
 *   • Choose what to sync, how often, and whether to import history.
 *   • Or drop a file (csv / parquet / excel / json) - auto-detected & typed.
 *
 * Advanced per-connector options (auth, headers, security protocol, keys)
 * live behind a single "Advanced" disclosure so the default path stays clean.
 *
 * Visual language matches the rest of the app (Segments / Signals): a
 * breadcrumb header, divider-bordered cards, compact type controls, and the
 * shared StatusChip / ConnectorLogo primitives.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
  Box, Paper, Typography, Button, TextField, Stack, Chip, MenuItem,
  CircularProgress, Alert, AlertTitle, Collapse,
  LinearProgress, useTheme, alpha,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import ScienceIcon from '@mui/icons-material/Science';

import config from '../../config';
import { getLocalUser } from '../../utils/localUser';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import { CONNECTOR_TYPES, APP_CONNECTORS, ConnectorLogo, typeLabel, computeBackfillLimits, scopeToBackfill } from './connectorMeta';
import TableSyncPicker from './TableSyncPicker';
import DatasetPlanReview from './DatasetPlanReview';
import WebhookGuide from './WebhookGuide';
import RestConnectPanel, { RestSyncOptions } from './RestConnectPanel';
import RawResponseDialog from './RawResponseDialog';
import SampleDatasetsDialog from './SampleDatasetsDialog';
import { detectSmartPaste, classifyAuth, defaultBackfillStart, presetById } from './restPresets';
import CodeIcon from '@mui/icons-material/Code';

import {
  fetchRequest as connectInspectRequest,
  clear as clearConnectInspect,
} from '../../redux/actions/datasources/connectInspect';
import {
  fetchRequest as connectCreateRequest,
  clear as clearConnectCreate,
} from '../../redux/actions/datasources/connectCreate';
import {
  fetchRequest as connectImportFileRequest,
  clear as clearConnectImportFile,
} from '../../redux/actions/datasources/connectImportFile';
import {
  fetchRequest as connectWebhookRequest,
  clear as clearConnectWebhook,
} from '../../redux/actions/datasources/connectWebhook';
import {
  fetchRequest as oauthStartRequest,
  clear as clearOauthStart,
} from '../../redux/actions/datasources/oauthStart';
import {
  fetchRequest as datasetInspectRequest,
  clear as clearDatasetInspect,
} from '../../redux/actions/datasources/datasetInspect';
import {
  fetchRequest as uploadAutoRequest,
  clear as clearUploadAuto,
} from '../../redux/actions/datasources/uploadAuto';
import {
  fetchRequest as connectInspectFileRequest,
  clear as clearConnectInspectFile,
} from '../../redux/actions/datasources/connectInspectFile';

const FILE_EXT = /\.(csv|tsv|parquet|xlsx|xls|json|jsonl)$/i;

// Stable fallback while the slice reducers boot (avoids new-object selectors).
const EMPTY_SLICE = { response: null, isLoading: false, error: null };

// Error payloads may arrive as strings or as structured {code, message}
// objects from the API — always render a string (an object child crashes
// React with a blank page).
const asText = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.message || v.error || JSON.stringify(v);
  return String(v);
};

// Fresh REST connect state (the panel mutates a copy of this).
const DEFAULT_REST = {
  method: 'GET',
  auth_type: 'none',
  token: '',
  username: '',
  password: '',
  api_key_name: 'X-API-Key',
  api_key_in: 'header',
  auth_headers: [{ name: '', value: '' }],
  oauth2: { token_url: '', client_id: '', client_secret: '', scope: '' },
  headers: [],
  body: '',
  presetId: null,
};

export default function ConnectData() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [phase, setPhase] = useState('pick'); // pick -> tables -> done
  const [type, setType] = useState(null);     // no source type picked yet
  const [connString, setConnString] = useState('');
  const [adv, setAdv] = useState({});
  const [advOpen, setAdvOpen] = useState(false);

  const [error, setError] = useState(null);
  const [errorHint, setErrorHint] = useState(null);

  const [inspectResult, setInspectResult] = useState(null);
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [frequency, setFrequency] = useState('hourly');
  const [importHistory, setImportHistory] = useState(true);
  const [plan, setPlan] = useState(null);
  const [fitToPlan, setFitToPlan] = useState(true);
  const [scopes, setScopes] = useState({});
  const [doneMessage, setDoneMessage] = useState('');

  const [dragOver, setDragOver] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);   // file awaiting Auto/Customize choice
  const [fileImport, setFileImport] = useState(null);      // { file_path, file_format, filename } for custom mapping
  const [dataset, setDataset] = useState(null);            // { dataset_id, plan } -> phase 'dataset'

  // ---- redux slices (one per operation) --------------------------------
  const inspectSlice = useSelector((s) => s.datasource_connect_inspect) || EMPTY_SLICE;
  const createSlice = useSelector((s) => s.datasource_connect_create) || EMPTY_SLICE;
  const importFileSlice = useSelector((s) => s.datasource_connect_import_file) || EMPTY_SLICE;
  const webhookSlice = useSelector((s) => s.datasource_connect_webhook) || EMPTY_SLICE;
  const oauthSlice = useSelector((s) => s.datasource_oauth_start) || EMPTY_SLICE;
  const datasetSlice = useSelector((s) => s.datasource_dataset_inspect) || EMPTY_SLICE;
  const uploadSlice = useSelector((s) => s.datasource_upload_auto) || EMPTY_SLICE;
  const inspectFileSlice = useSelector((s) => s.datasource_connect_inspect_file) || EMPTY_SLICE;

  // Same visible flags as the old local state, now derived from the slices.
  const inspecting = inspectSlice.isLoading || inspectFileSlice.isLoading;
  const creating = createSlice.isLoading || importFileSlice.isLoading || webhookSlice.isLoading;
  // Auto file import state: { status, progress, name, format, detected, rows, error }
  const uploadState = uploadSlice.response || null;
  // Multi-file staging/planning progress while the AI plans the dataset.
  const datasetState = (datasetSlice.response
    && (datasetSlice.response.status === 'staging' || datasetSlice.response.status === 'planning'))
    ? datasetSlice.response : null;

  // Everything here was component-local before the saga migration — reset the
  // slices on unmount so a revisit starts fresh (this also cancels any
  // still-running upload/staging pollers).
  useEffect(() => () => {
    dispatch(clearConnectInspect());
    dispatch(clearConnectCreate());
    dispatch(clearConnectImportFile());
    dispatch(clearConnectWebhook());
    dispatch(clearOauthStart());
    dispatch(clearDatasetInspect());
    dispatch(clearUploadAuto());
    dispatch(clearConnectInspectFile());
  }, [dispatch]);

  // Webhook config + result (the one push source type).
  const [webhookCfg, setWebhookCfg] = useState({ name: '' });
  const webhookResult = webhookSlice.response || null;

  // One-click app (OAuth) state: which app we're waiting on, and the connected
  // account handle the callback hands back ({provider, token_ref, label}).
  const [oauthWaiting, setOauthWaiting] = useState(null);
  const [saas, setSaas] = useState(null);

  // REST connector state: request config (auth/headers/body), how to page
  // through results, and the historical-vs-live capture choice.
  const [rest, setRest] = useState(DEFAULT_REST);
  const [restPagination, setRestPagination] = useState({ type: 'none' });
  const [restSync, setRestSync] = useState({ mode: 'live', param: '', start: defaultBackfillStart() });
  const [rawOpen, setRawOpen] = useState(false);
  const [smartPaste, setSmartPaste] = useState(null); // 'curl' | 'http' | null
  const [samplesOpen, setSamplesOpen] = useState(false); // sample datasets catalog

  const typeMeta = useMemo(() => CONNECTOR_TYPES.find((t) => t.id === type) || null, [type]);

  // Click a tile to select it; click the selected tile again to collapse the
  // connect panel.
  const selectTile = useCallback((tileId) => {
    setType((prev) => (prev === tileId ? null : tileId));
    setError(null);
    setSmartPaste(null);
  }, []);

  // REST smart paste: when the URL box receives a `curl ...` command or a raw
  // HTTP request (the "GET /path HTTP/1.1" format API docs show), parse it and
  // fill the URL, method, headers, auth and body automatically.
  const handleConnStringChange = useCallback((text) => {
    if (type === 'rest') {
      const det = detectSmartPaste(text);
      if (det) {
        const { request } = det;
        const cls = classifyAuth(request.headers, { username: request.username, password: request.password });
        setConnString(request.url);
        setRest((r) => ({
          ...r,
          presetId: null,
          method: request.method === 'POST' ? 'POST' : 'GET',
          auth_type: cls.auth_type,
          token: cls.token || '',
          username: cls.username || '',
          password: cls.password || '',
          auth_headers: cls.auth_headers.length ? cls.auth_headers : r.auth_headers,
          headers: cls.custom,
          body: request.body || '',
        }));
        setSmartPaste(det.kind);
        return;
      }
    }
    setConnString(text);
  }, [type]);

  // Apply a provider preset: URL + auth scaffold + pagination + incremental
  // param + a sensible poll frequency. The user only fills in their keys.
  const applyPreset = useCallback((p) => {
    setConnString(p.url);
    const auth = p.auth || { type: 'none' };
    setRest({
      ...DEFAULT_REST,
      presetId: p.id,
      auth_type: auth.type,
      api_key_in: auth.in || 'header',
      api_key_name: auth.name || 'X-API-Key',
      auth_headers: auth.type === 'headers'
        ? auth.headers.map((h) => ({ name: h.name, value: '' }))
        : [{ name: '', value: '' }],
    });
    setRestPagination(p.pagination ? { ...p.pagination } : { type: 'none' });
    setRestSync({
      mode: p.incremental ? 'historical_live' : 'live',
      param: p.incremental?.param || '',
      start: defaultBackfillStart(),
    });
    if (p.frequency) setFrequency(p.frequency);
    setSmartPaste(null);
    setError(null);
  }, []);

  const card = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    bgcolor: theme.palette.background.paper,
  };
  const sectionLabel = {
    fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
    color: 'text.secondary', mb: 1,
  };

  // ---- build request body (shared by inspect + create) ----------------
  const buildBody = useCallback(() => {
    // Connected one-click app: the stored OAuth token IS the config.
    if (saas) {
      return {
        connector_type: saas.provider,
        name: saas.label,
        credentials: { auth_type: 'oauth', token_ref: saas.token_ref },
      };
    }
    const body = { connector_type: type };
    if (connString.trim()) body.connection_string = connString.trim();

    const creds = {};
    const conn = {};
    if (adv.password) creds.password = adv.password;
    if (adv.username) creds.username = adv.username;
    if (adv.access_key_id) creds.access_key_id = adv.access_key_id;
    if (adv.secret_access_key) creds.secret_access_key = adv.secret_access_key;
    if (adv.token) creds.token = adv.token;

    if (type === 'bigquery' && adv.service_account_json) body.service_account_json = adv.service_account_json;
    if (type === 'bigquery' && adv.endpoint) conn.endpoint = adv.endpoint;
    if ((type === 'postgres' || type === 'mysql' || type === 'sqlserver')) {
      if (adv.sslmode) conn.options = { ...(conn.options || {}), sslmode: adv.sslmode };
      if (adv.schema) conn.options = { ...(conn.options || {}), schema: adv.schema };
    }
    if (type === 'rest') {
      conn.method = rest.method || 'GET';
      const customHeaders = (rest.headers || []).filter((h) => h.name);
      if (customHeaders.length) {
        conn.headers = Object.fromEntries(customHeaders.map((h) => [h.name, h.value ?? '']));
      }
      if (rest.method === 'POST' && rest.body) conn.body = rest.body;

      // Auth -> credentials in the exact shape the backend connector reads.
      if (rest.auth_type === 'bearer' && rest.token) {
        creds.auth_type = 'bearer';
        creds.token = rest.token;
      } else if (rest.auth_type === 'apikey' && rest.token) {
        creds.auth_type = 'apikey';
        creds.api_key = rest.token;
        creds.api_key_header = rest.api_key_name || 'X-API-Key';
        creds.api_key_in = rest.api_key_in || 'header';
        delete creds.token; // generic token line above must not force bearer
      } else if (rest.auth_type === 'headers') {
        const rows = (rest.auth_headers || []).filter((h) => h.name && h.value);
        if (rows.length) {
          creds.auth_type = 'headers';
          creds.auth_headers = Object.fromEntries(rows.map((h) => [h.name, h.value]));
        }
      } else if (rest.auth_type === 'basic' && rest.username) {
        creds.auth_type = 'basic';
        creds.username = rest.username;
        creds.password = rest.password || '';
      } else if (rest.auth_type === 'oauth2_client_credentials' && rest.oauth2?.token_url) {
        creds.auth_type = 'oauth2_client_credentials';
        creds.token_url = rest.oauth2.token_url;
        creds.client_id = rest.oauth2.client_id || '';
        creds.client_secret = rest.oauth2.client_secret || '';
        if (rest.oauth2.scope) creds.scope = rest.oauth2.scope;
      }

      // Window the SAMPLE request for presets that declare it (e.g. Alpaca
      // defaults `start` to "today", which is empty pre-market - so field
      // detection would see zero rows without this).
      const preset = presetById(rest.presetId);
      if (preset?.sampleWindowDays && preset?.incremental?.param) {
        const d = new Date();
        d.setDate(d.getDate() - preset.sampleWindowDays);
        body.sample_params = { [preset.incremental.param]: d.toISOString().slice(0, 10) };
      }
    }
    if (type === 'kafka') {
      if (adv.security_protocol) conn.security_protocol = adv.security_protocol;
      if (adv.sasl_mechanism) conn.sasl_mechanism = adv.sasl_mechanism;
    }
    if (type === 's3') {
      if (adv.endpoint_url) conn.endpoint_url = adv.endpoint_url;
      if (adv.region) conn.region = adv.region;
    }
    if (type === 'webhook') {
      if (adv.name) conn.name = adv.name;
      if (adv.signature_secret) creds.signature_secret = adv.signature_secret;
    }
    if (Object.keys(creds).length) body.credentials = creds;
    if (Object.keys(conn).length) body.connection = conn;
    return body;
  }, [type, connString, adv, rest, saas]);

  // ---- inspect ---------------------------------------------------------
  // `bodyOverride` lets the OAuth callback inspect immediately (before the
  // `saas` state commit lands in buildBody). Guarded so onClick events don't
  // masquerade as a body. The saga also creates sources with nothing to map
  // (S3 / Kafka) in the same flow, so a single spinner covers both steps.
  const runInspect = useCallback((bodyOverride) => {
    const body = (bodyOverride && bodyOverride.connector_type) ? bodyOverride : buildBody();
    setError(null); setErrorHint(null);
    dispatch(connectInspectRequest({ body }));
  }, [buildBody, dispatch]);

  useEffect(() => {
    const data = inspectSlice.response;
    if (!data) return;
    dispatch(clearConnectInspect());

    if (data.status === 'failed' || (data.test && data.test.success === false)) {
      setError(data.test?.error || data.error || 'Connection failed.');
      setErrorHint('Check the host, port, database and credentials. Add secrets in Advanced if needed.');
      setAdvOpen(true);
      return;
    }

    // Rare fallback: a source with genuinely nothing to map was created
    // directly by the saga (every normal source type now returns tables).
    if (data.created) {
      setDoneMessage('Source connected.');
      setPhase('done');
      setTimeout(() => navigate(`/datasources/${data.created.source_id}`), 1200);
      return;
    }

    setInspectResult(data);
    const tbls = data.tables || [];

    // DB/Warehouse return tables; REST returns a single detected "response"
    // mapping. Either way, if we have fields to map, show the field mapper.
    setTables(tbls);
    setPlan(data.plan || null);
    const sel = {};
    // Pre-select tables with a time column; always pre-select the single REST
    // response so the user can map + connect in one step.
    tbls.forEach((t) => { sel[t.table_name] = t.rest ? true : !!t.timestamp_column; });
    setSelected(sel);
    setExpanded(tbls.length === 1 ? tbls[0].table_name : null);
    setPhase('tables');
  }, [inspectSlice.response, dispatch, navigate]);

  useEffect(() => {
    if (inspectSlice.error) {
      setError(inspectSlice.error);
      dispatch(clearConnectInspect());
    }
  }, [inspectSlice.error, dispatch]);

  // ---- one-click apps (OAuth) ------------------------------------------
  // Which providers actually have OAuth app credentials on THIS deployment.
  // A 'live' tile without configured credentials renders as coming soon
  // instead of failing at click time. null = directory not loaded (keep the
  // static statuses).
  const [oauthDirectory, setOauthDirectory] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${config.API_URL}/ingestion/oauth/providers`, { headers: getLocalUser() })
      .then((res) => {
        const d = res.data?.data || res.data;
        if (!cancelled && d?.providers) setOauthDirectory(d.providers);
      })
      .catch(() => { /* directory unavailable — fall back to static statuses */ });
    return () => { cancelled = true; };
  }, []);
  const appIsLive = useCallback(
    (app) => app.status === 'live' && oauthDirectory?.[app.id]?.configured !== false,
    [oauthDirectory],
  );

  // Click "Connect" on an app tile -> the saga fetches the authorize_url ->
  // open the provider's consent screen in a popup. The API callback page
  // posts {type:'oauth_done', provider, token_ref | source_id | error} back
  // to this window.
  const connectApp = useCallback((app) => {
    if (!appIsLive(app) || oauthWaiting) return;
    setError(null); setErrorHint(null);
    dispatch(oauthStartRequest({ appId: app.id, label: app.label }));
  }, [dispatch, oauthWaiting, appIsLive]);

  useEffect(() => {
    const resp = oauthSlice.response;
    if (!resp) return;
    dispatch(clearOauthStart());
    const { app, data } = resp;
    if (data.status !== 'ok' || !data.authorize_url) {
      setError(data.error || `Couldn't start ${app.label} authorization.`);
      return;
    }
    const popup = window.open(
      data.authorize_url, `connect_${app.id}`,
      'width=680,height=780,menubar=no,toolbar=no',
    );
    if (!popup) {
      setError('The sign-in popup was blocked. Allow popups for this site and try again.');
      return;
    }
    setOauthWaiting(app.id);
    // Clear the spinner if the user closes the popup without finishing.
    // (The oauth_done message beats this check when the flow completes.)
    const watch = setInterval(() => {
      if (popup.closed) {
        clearInterval(watch);
        setOauthWaiting((w) => (w === app.id ? null : w));
      }
    }, 800);
  }, [oauthSlice.response, dispatch]);

  useEffect(() => {
    if (oauthSlice.error) {
      setError(oauthSlice.error);
      dispatch(clearOauthStart());
    }
  }, [oauthSlice.error, dispatch]);

  useEffect(() => {
    const onMessage = (ev) => {
      const d = ev.data || {};
      if (d.type !== 'oauth_done') return;
      // The callback page is served by the API, so only trust its origin.
      try {
        if (ev.origin !== new URL(config.API_URL).origin) return;
      } catch (e) { /* relative API URL - skip the origin gate */ }
      setOauthWaiting(null);
      if (d.error) {
        setError(`Authorization failed: ${d.error}`);
        return;
      }
      if (d.token_ref) {
        // Source-kind provider (e.g. Stripe): tokens stored, now inspect the
        // account and drop into the normal "pick what to sync" step.
        const app = APP_CONNECTORS.find((a) => a.id === d.provider);
        const label = app?.label || d.provider;
        setSaas({ provider: d.provider, token_ref: d.token_ref, label });
        runInspect({
          connector_type: d.provider,
          name: label,
          credentials: { auth_type: 'oauth', token_ref: d.token_ref },
        });
      } else if (d.source_id) {
        // Event-kind provider: the source already exists.
        navigate(`/datasources/${d.source_id}`);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [runInspect, navigate]);

  // ---- create a Webhook source (push) ---------------------------------
  const runCreateWebhook = useCallback(() => {
    setError(null);
    const body = {
      mode: 'push',
      connector_type: 'webhook',
      name: webhookCfg.name || 'Webhook',
      // Uniform vocabulary: the payload carries ``occurred_at`` (when it
      // happened). The recorded time is set automatically on receipt. The
      // path only overrides the field NAME if a provider uses a different one.
      event_config: {
        stream: webhookCfg.name || 'webhook',
        text_path: 'text',
        id_path: 'id',
        ...(webhookCfg.occurredAtPath?.trim() ? { occurred_at_path: webhookCfg.occurredAtPath.trim() } : {}),
      },
    };
    dispatch(connectWebhookRequest({ body }));
  }, [webhookCfg, dispatch]);

  useEffect(() => {
    if (webhookResult) setPhase('webhook');
  }, [webhookResult]);

  useEffect(() => {
    if (webhookSlice.error) {
      setError(webhookSlice.error);
      dispatch(clearConnectWebhook());
    }
  }, [webhookSlice.error, dispatch]);

  // ---- create (tables / REST fields / custom file mapping) ------------
  const runCreate = useCallback(() => {
    setError(null);
    // Custom file import: map the staged file with the user's chosen fields.
    if (fileImport) {
      const t = tables[0];
      const field_mappings = (t.columns || []).filter((c) => c.included || c.is_event).map((c) => ({
        source_field: c.source_field, target_key: c.target_key, data_type: c.data_type,
        display_name: c.display_name || undefined,
        is_timestamp: c.is_timestamp, is_entity: c.is_entity, is_event: c.is_event || false,
        original_index: c.original_index,
      }));
      // Import scope (most-recent N rows / date range) -> file worker options.
      const scopeOpts = importHistory ? scopeToBackfill(scopes[t.table_name], null) : {};
      dispatch(connectImportFileRequest({
        body: {
          file_path: fileImport.file_path, file_format: fileImport.file_format,
          filename: fileImport.filename, field_mappings,
          options: { delete_existing: false, skip_duplicates: true, ...scopeOpts },
        },
      }));
      return;
    }
    // If importing history would exceed the plan and the user opted to "fit",
    // cap each table's first backfill to its share of the remaining budget.
    const remaining = plan?.signals_remaining;
    const limits = (importHistory && fitToPlan && remaining != null)
      ? computeBackfillLimits(tables, selected, remaining) : {};

    // REST capture mode drives history + scheduling:
    //   historical_live -> backfill from the start date, then poll forward
    //   live            -> seed the watermark at "now", poll forward only
    //   snapshot        -> one manual run, no schedule
    const isRestCreate = inspectResult?.connector_type === 'rest' || inspectResult?.connector_type === 'graphql';
    const effImportHistory = isRestCreate ? restSync.mode !== 'live' : importHistory;

    const chosen = tables.filter((t) => selected[t.table_name]).map((t) => ({
        table_name: t.table_name,
        frequency: (t.rest && restSync.mode === 'snapshot') ? 'manual' : frequency,
        timestamp_column: t.timestamp_column,
        entity_column: t.entity_column,
        watermark_column: t.watermark_column,
        watermark_type: t.watermark_type,
        // REST sources carry the response shape so the job can re-flatten it.
        rest: t.rest || false,
        array_path: t.array_path,
        // Universal shape spec (records / columnar / tuple / map / single
        // record) - the connector replays it to reshape any response into rows.
        shape: t.shape,
        encoding: t.encoding,
        // S3 file-group / Kafka topic specifics detected at inspect time -
        // the backend turns these into the right sync job config.
        ...(t.s3 ? { s3: t.s3 } : {}),
        ...(t.kafka ? { kafka: t.kafka } : {}),
        ...(t.rest ? {
          method: rest.method || 'GET',
          sync_mode: restSync.mode,
          ...(restSync.param.trim() && restSync.mode !== 'snapshot' ? {
            incremental: {
              param: restSync.param.trim(),
              watermark_type: 'datetime',
              ...(restSync.mode === 'historical_live' && restSync.start
                ? { initial_value: restSync.start } : {}),
            },
          } : {}),
          ...(restPagination.type && restPagination.type !== 'none'
            ? { pagination: restPagination } : {}),
        } : {}),
        ...(importHistory && !t.rest ? scopeToBackfill(scopes[t.table_name], limits[t.table_name]) : {}),
        field_mappings: (t.columns || []).filter((c) => c.included || c.is_event).map((c) => ({
          source_field: c.source_field, target_key: c.target_key, data_type: c.data_type,
          display_name: c.display_name || undefined,
          is_timestamp: c.is_timestamp, is_entity: c.is_entity, is_event: c.is_event || false,
          parse_options: {},
        })),
      }));
    const body = { ...buildBody(), name: inspectResult?.label, import_historical: effImportHistory, tables: chosen };
    dispatch(connectCreateRequest({ body }));
  }, [tables, selected, frequency, buildBody, inspectResult, importHistory, plan, fitToPlan, scopes, fileImport, dispatch, rest, restSync, restPagination]);

  // Source created (tables / REST fields).
  useEffect(() => {
    const out = createSlice.response;
    if (!out) return;
    dispatch(clearConnectCreate());
    setDoneMessage(out.message || 'Connected.');
    setPhase('done');
    setTimeout(() => navigate(`/datasources/${out.source_id}`), 1500);
  }, [createSlice.response, dispatch, navigate]);

  useEffect(() => {
    if (createSlice.error) {
      setError(createSlice.error);
      dispatch(clearConnectCreate());
    }
  }, [createSlice.error, dispatch]);

  // Custom file import started.
  useEffect(() => {
    if (!importFileSlice.response) return;
    dispatch(clearConnectImportFile());
    setDoneMessage('Importing your file with the custom mapping.');
    setPhase('done');
    setTimeout(() => navigate('/datasources/uploads'), 1400);
  }, [importFileSlice.response, dispatch, navigate]);

  useEffect(() => {
    if (importFileSlice.error) {
      setError(importFileSlice.error);
      dispatch(clearConnectImportFile());
    }
  }, [importFileSlice.error, dispatch]);

  // ---- file upload -----------------------------------------------------
  // One file -> the Auto/Customize choice. Several files at once -> the AI
  // dataset planner analyzes them TOGETHER (key collisions, duplicates,
  // leakage, encodings, derived series) and returns a reviewable plan.
  // The whole flow (stage one file per request, then /datasets/inspect for
  // the one-shot AI plan) runs in the datasetInspect saga; staging/planning
  // progress streams back through the slice.
  const inspectDataset = useCallback((files) => {
    dispatch(datasetInspectRequest({ files }));
  }, [dispatch]);

  useEffect(() => {
    const resp = datasetSlice.response;
    if (!resp || resp.status !== 'ready') return;
    dispatch(clearDatasetInspect());
    setDataset({ dataset_id: resp.dataset_id, plan: resp.plan });
    setPhase('dataset');
  }, [datasetSlice.response, dispatch]);

  useEffect(() => {
    if (datasetSlice.error) {
      setError(datasetSlice.error);
      dispatch(clearDatasetInspect());
    }
  }, [datasetSlice.error, dispatch]);

  const handleFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const bad = files.filter((f) => !FILE_EXT.test(f.name));
    if (bad.length) {
      setError(`Unsupported file${bad.length > 1 ? 's' : ''}: ${bad.map((f) => f.name).join(', ')}. Use CSV, Parquet, Excel or JSON.`);
      return;
    }
    setError(null);
    if (files.length === 1) {
      setPendingFile(files[0]);
      return;
    }
    inspectDataset(files);
  }, [inspectDataset]);


  // Auto: detect + type + import in one shot (upload + execution polling
  // both live in the uploadAuto saga; the slice's response is uploadState).
  const uploadAuto = useCallback((file) => {
    setPendingFile(null);
    dispatch(uploadAutoRequest({ file }));
  }, [dispatch]);

  // Customize: stage + detect, then show the field mapper before importing.
  const inspectFile = useCallback((file) => {
    setError(null);
    dispatch(connectInspectFileRequest({ file }));
  }, [dispatch]);

  useEffect(() => {
    const data = inspectFileSlice.response;
    if (!data) return;
    dispatch(clearConnectInspectFile());
    if (!data.table) { setError('Could not read that file.'); return; }
    setPendingFile(null);
    setFileImport({ file_path: data.file_path, file_format: data.file_format, filename: data.filename });
    setInspectResult({ label: data.filename, connector_type: 'file' });
    setTables([data.table]);
    setSelected({ [data.table.table_name]: true });
    setExpanded(data.table.table_name);
    setPlan(null);
    setPhase('tables');
  }, [inspectFileSlice.response, dispatch]);

  useEffect(() => {
    if (inspectFileSlice.error) {
      setError(inspectFileSlice.error);
      dispatch(clearConnectInspectFile());
    }
  }, [inspectFileSlice.error, dispatch]);

  // ---- per-column edits in tables phase --------------------------------
  const updateColumn = (tableName, idx, patch) => {
    setTables((prev) => prev.map((t) => {
      if (t.table_name !== tableName) return t;
      const columns = t.columns.map((c, i) => {
        if (i !== idx) {
          if (patch.is_timestamp) return { ...c, is_timestamp: false };
          if (patch.is_entity) return { ...c, is_entity: false };
          return c;
        }
        return { ...c, ...patch };
      });
      const ts = columns.find((c) => c.is_timestamp);
      const ent = columns.find((c) => c.is_entity);
      return { ...t, columns, timestamp_column: ts?.source_field || null, entity_column: ent?.source_field || null };
    }));
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const Header = (
    <PageBreadcrumbs items={[
      { text: 'Data Sources', link: '/datasources', icon: <StorageIcon /> },
      { text: 'Connect' },
    ]} />
  );

  // ===================================================== WEBHOOK CREATED
  if (phase === 'webhook' && webhookResult) {
    return (
      <Box sx={{ maxWidth: 820, mx: 'auto' }}>
        {Header}
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Webhook ready</Typography>
        </Stack>
        <WebhookGuide
          eventsUrl={webhookResult.webhook_url_events || webhookResult.webhook_url}
          signalUrl={webhookResult.webhook_url_signal}
          secret={webhookResult.webhook_secret}
          eventConfig={{
            text_path: 'text',
            ...(webhookCfg.occurredAtPath?.trim() ? { occurred_at_path: webhookCfg.occurredAtPath.trim() } : {}),
          }}
          card={card}
        />
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="contained" sx={{ textTransform: 'none' }}
            onClick={() => navigate(`/datasources/${webhookResult.source_id}`)}>Done</Button>
        </Stack>
      </Box>
    );
  }

  // ===================================================== DATASET (multi-file)
  if (phase === 'dataset' && dataset) {
    return (
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        {Header}
        <DatasetPlanReview
          datasetId={dataset.dataset_id}
          plan={dataset.plan}
          onBack={() => { setPhase('pick'); setDataset(null); }}
          onDone={() => navigate('/datasources/uploads')}
        />
      </Box>
    );
  }

  // ===================================================== DONE
  if (phase === 'done') {
    return (
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        {Header}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
          <Typography variant="h5" sx={{ mt: 2, fontWeight: 700 }}>You're connected</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>{doneMessage}</Typography>
          <CircularProgress size={18} sx={{ mt: 3 }} />
        </Box>
      </Box>
    );
  }

  // ===================================================== TABLES
  if (phase === 'tables') {
    const isRest = inspectResult?.connector_type === 'rest' || inspectResult?.connector_type === 'graphql';
    const isFile = !!fileImport;
    // A time column is mandatory on every selected source.
    const missingNames = tables.filter((t) => selected[t.table_name] && !t.timestamp_column).map((t) => t.table_name);
    const missingTimestamp = missingNames.length ? missingNames : null;
    return (
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        {Header}
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
          <ConnectorLogo type={inspectResult?.connector_type} size={28} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {inspectResult?.label || 'Connected'}
          </Typography>
          <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label="Connected" />
          {isRest && inspectResult?.raw_sample != null && (
            <Button size="small" variant="outlined" startIcon={<CodeIcon />} onClick={() => setRawOpen(true)}
              sx={{ textTransform: 'none', ml: 'auto' }}>
              View raw response
            </Button>
          )}
        </Stack>
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2.5 }}>
          {isFile
            ? 'We read your file and detected the columns. Pick what to import, set the time/id columns, and tick "event" on any text column to score it as an event.'
            : isRest
              ? 'We read a sample response and detected your fields. Pick what to sync, set the time field, and tick "event" on any field that should be scored as an event.'
              : saas
                ? `Your ${saas.label} account is connected and every dataset is pre-mapped. Choose what to sync — history imports first, then we keep it fresh automatically.`
                : 'Schema read and auto-mapped. Choose what to sync — you can fine-tune any column. Tick "event" on any field to score it as an event.'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{asText(error)}</Alert>}

        <TableSyncPicker
          tables={tables} selected={selected} setSelected={setSelected}
          expanded={expanded} setExpanded={setExpanded}
          frequency={frequency} setFrequency={setFrequency}
          importHistory={importHistory} setImportHistory={setImportHistory}
          updateColumn={updateColumn}
          plan={isRest ? null : plan} fitToPlan={fitToPlan} setFitToPlan={setFitToPlan}
          scopes={scopes} setScope={(name, patch) => setScopes((s) => ({ ...s, [name]: { ...s[name], ...patch } }))}
          hideHistoryToggle={isRest}
        />

        {/* REST: historical-vs-live capture + pagination, replacing the
            simple "import history" switch DB sources use. */}
        {isRest && (
          <RestSyncOptions
            sync={restSync} setSync={setRestSync}
            pagination={restPagination} setPagination={setRestPagination}
            theme={theme}
          />
        )}

        {missingTimestamp && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Every source needs a time column. Open <strong>Configure</strong> on{' '}
            {missingTimestamp.length === 1 ? <strong>{missingTimestamp[0]}</strong> : `${missingTimestamp.length} selected items`}
            {' '}and tap <strong>time</strong> on the timestamp column.
          </Alert>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 3 }} justifyContent="space-between">
          <Button onClick={() => { setPhase('pick'); setTables([]); setFileImport(null); setSaas(null); }} disabled={creating} sx={{ textTransform: 'none' }}>Back</Button>
          <Button variant="contained" disabled={selectedCount === 0 || creating || !!missingTimestamp} onClick={runCreate}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <BoltIcon />} sx={{ textTransform: 'none', px: 3 }}>
            {creating ? (isFile ? 'Importing…' : 'Connecting…') : (isFile ? 'Import file' : isRest ? 'Connect & start syncing' : `Connect & sync ${selectedCount} table${selectedCount === 1 ? '' : 's'}`)}
          </Button>
        </Stack>

        <RawResponseDialog
          open={rawOpen}
          onClose={() => setRawOpen(false)}
          data={inspectResult?.raw_sample}
          truncated={inspectResult?.raw_sample_truncated}
          title={`Raw response · ${inspectResult?.label || 'API'}`}
        />
      </Box>
    );
  }

  // ===================================================== PICK
  // File dropzone — drop ONE file for the quick Auto/Customize flow, or
  // SEVERAL at once and the AI plans them together as one dataset.
  const uploaderCard = (
    <Paper elevation={0}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!datasetState) handleFiles(e.dataTransfer.files); }}
      onClick={() => (!uploadState && !pendingFile && !datasetState) && fileInputRef.current?.click()}
      sx={{
        ...card, p: 3.5, textAlign: 'center', cursor: (uploadState || pendingFile || datasetState) ? 'default' : 'pointer',
        borderStyle: 'dashed', borderWidth: 1.5,
        borderColor: dragOver ? theme.palette.primary.main : theme.palette.divider,
        bgcolor: dragOver ? alpha(theme.palette.primary.main, 0.06) : theme.palette.background.paper,
        transition: '0.15s',
      }}>
      <input ref={fileInputRef} type="file" hidden multiple accept=".csv,.tsv,.parquet,.xlsx,.xls,.json,.jsonl"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
      {!uploadState && !pendingFile && !datasetState && (
        <>
          <CloudUploadIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
          <Typography sx={{ mt: 0.5, fontWeight: 600, fontSize: 14 }}>Drop files or click to browse</Typography>
          <Typography variant="caption" color="text.secondary">CSV · Parquet · Excel · JSON</Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            Select several files at once to import them together as one dataset
          </Typography>
        </>
      )}
      {datasetState && (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={22} sx={{ mb: 1 }} />
          {datasetState.status === 'staging' ? (
            <>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>
                Uploading file {datasetState.index} of {datasetState.count}: {datasetState.current}
              </Typography>
              <LinearProgress variant="determinate" value={datasetState.progress || 0}
                sx={{ mt: 1, mx: 'auto', maxWidth: 360, height: 6, borderRadius: 1 }} />
            </>
          ) : (
            <>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                Preparing {datasetState.count} files as one dataset…
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Resolving shared keys, duplicates and column mappings across files
              </Typography>
            </>
          )}
        </Box>
      )}
      {!uploadState && pendingFile && !datasetState && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }} noWrap>{pendingFile.name}</Typography>
          <Typography variant="caption" color="text.secondary">How should we map it?</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 1.5 }}>
            <Button variant="contained" sx={{ textTransform: 'none' }}
              startIcon={<BoltIcon />} disabled={inspecting} onClick={() => uploadAuto(pendingFile)}>
              Auto-map & import
            </Button>
            <Button variant="outlined" sx={{ textTransform: 'none' }} disabled={inspecting}
              startIcon={inspecting ? <CircularProgress size={16} /> : <TuneIcon />} onClick={() => inspectFile(pendingFile)}>
              {inspecting ? 'Reading file…' : 'Customize fields'}
            </Button>
          </Stack>
          <Button size="small" sx={{ textTransform: 'none', mt: 1 }} onClick={() => setPendingFile(null)} disabled={inspecting}>Cancel</Button>
        </Box>
      )}
      {uploadState && (
        <Box sx={{ textAlign: 'left' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {uploadState.status === 'completed' ? <CheckCircleIcon color="success" />
              : uploadState.status === 'failed' ? <Box sx={{ color: 'error.main' }}>✕</Box>
                : <CircularProgress size={16} />}
            <Typography sx={{ fontWeight: 600, flex: 1, fontSize: 14 }} noWrap>{uploadState.name}</Typography>
            {uploadState.format && <Chip size="small" label={uploadState.format} sx={{ height: 20, fontSize: 11 }} />}
          </Stack>
          {uploadState.status !== 'failed' && <LinearProgress variant="determinate" value={uploadState.progress || 0} sx={{ mt: 1.25, borderRadius: 1, height: 6 }} />}
          {uploadState.status === 'processing' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
              Auto-typing & importing… {uploadState.rows ? `${uploadState.rows.toLocaleString()} rows` : ''}
            </Typography>
          )}
          {uploadState.status === 'completed' && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} alignItems="center">
              <Typography variant="body2" color="success.main" sx={{ flex: 1 }}>Imported {(uploadState.rows || 0).toLocaleString()} rows.</Typography>
              <Button size="small" sx={{ textTransform: 'none' }} onClick={() => navigate('/datasources/uploads')}>View imports</Button>
              <Button size="small" sx={{ textTransform: 'none' }} onClick={() => dispatch(clearUploadAuto())}>Import another</Button>
            </Stack>
          )}
          {uploadState.status === 'failed' && <Alert severity="error" sx={{ mt: 1.25 }} onClose={() => dispatch(clearUploadAuto())}>{uploadState.error}</Alert>}
          {uploadState.detected?.timestamp_column && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Detected time: <strong>{uploadState.detected.timestamp_column}</strong>
              {uploadState.detected.entity_column ? ` · entity: ${uploadState.detected.entity_column}` : ''}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto' }}>
      {Header}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {errorHint && <AlertTitle sx={{ fontWeight: 700 }}>{asText(error)}</AlertTitle>}
          {asText(errorHint || error)}
        </Alert>
      )}

      {/* File upload first: the fastest way to get data in. */}
      <Typography sx={sectionLabel}>Upload files</Typography>
      {uploaderCard}

      {/* One-click apps: OAuth "Connect with ..." tiles. Live apps start the
          consent popup; the rest are on the roadmap and shown as coming soon. */}
      <Typography sx={{ ...sectionLabel, mt: 3.5 }}>Connect an app</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5, mb: 1.25 }}>
        One click — sign in, pick what to sync, and your data starts flowing.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)' }, gap: 1, mb: 3 }}>
        {APP_CONNECTORS.map((a) => {
          const live = appIsLive(a);
          const waiting = oauthWaiting === a.id;
          return (
            <Paper key={a.id} elevation={0} onClick={() => live && connectApp(a)}
              sx={{
                ...card, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.25,
                cursor: live ? 'pointer' : 'default',
                position: 'relative',
                transition: '0.12s',
                ...(live
                  ? { '&:hover': { borderColor: theme.palette.primary.main, boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}` } }
                  : { opacity: 0.62 }),
              }}>
              <ConnectorLogo type={a.id} size={34} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{a.label}</Typography>
                  {!live && (
                    <Chip label="Coming soon" size="small"
                      sx={{ height: 16, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.2,
                            bgcolor: alpha(theme.palette.text.secondary, 0.1), color: 'text.secondary' }} />
                  )}
                </Stack>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>{a.tagline}</Typography>
              </Box>
              {waiting && <CircularProgress size={16} sx={{ flexShrink: 0 }} />}
              {live && !waiting && (
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'primary.main', flexShrink: 0 }}>
                  Connect
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>

      {/* Connector type tiles — clicking one reveals its connect panel below. */}
      <Typography sx={sectionLabel}>Or choose a source type</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1, mb: 3 }}>
        {CONNECTOR_TYPES.map((t) => {
          const active = type === t.id;
          return (
            <Paper key={t.id} elevation={0} onClick={() => selectTile(t.id)}
              sx={{
                ...card, p: 1.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.25,
                borderColor: active ? theme.palette.primary.main : theme.palette.divider,
                bgcolor: active ? alpha(theme.palette.primary.main, 0.06) : theme.palette.background.paper,
                transition: '0.12s',
                '&:hover': { borderColor: theme.palette.primary.main },
              }}>
              <ConnectorLogo type={t.id} size={30} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{t.label}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>{t.category}</Typography>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Connection input — only shown once a source type is selected. */}
      {type && (<>
      <Typography sx={sectionLabel}>
        {type === 'webhook' ? 'Set up webhook' : `Connect ${typeLabel(type)}`}
      </Typography>

      {type === 'webhook' ? (
        <Paper elevation={0} sx={{ ...card, p: 2.5, mb: 2 }}>
          <Stack spacing={1.5}>
            <TextField fullWidth size="small" label="Name" placeholder="e.g. Breaking news, Order events" autoFocus
              value={webhookCfg.name} onChange={(e) => setWebhookCfg((s) => ({ ...s, name: e.target.value }))} />

            {/* Send ``occurred_at`` (when it happened) in the payload — read
                directly. The recorded time is set automatically on receipt.
                This field only overrides the field NAME if your provider uses a
                different one. Tucked under Advanced. */}
            <Box>
              <Button size="small" onClick={() => setAdvOpen((o) => !o)} startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
                endIcon={<ExpandMoreIcon sx={{ fontSize: 16, transform: advOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
                sx={{ textTransform: 'none', color: 'text.secondary' }}>
                Event timing (advanced)
              </Button>
              <Collapse in={advOpen}>
                <Stack spacing={1.5} sx={{ pt: 1.5 }}>
                  <TextField fullWidth size="small" label="occurred_at field name (optional)"
                    placeholder="occurred_at"
                    value={webhookCfg.occurredAtPath || ''}
                    onChange={(e) => setWebhookCfg((s) => ({ ...s, occurredAtPath: e.target.value }))}
                    helperText="We read occurred_at from the payload (when it happened). Set this only if your provider names that field differently." />
                </Stack>
              </Collapse>
            </Box>

            <Button fullWidth variant="contained" sx={{ py: 1.1, textTransform: 'none' }}
              disabled={creating || !webhookCfg.name.trim()} onClick={runCreateWebhook}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <BoltIcon />}>
              {creating ? 'Creating…' : 'Create webhook'}
            </Button>
          </Stack>
        </Paper>
      ) : (
      <Paper elevation={0} sx={{ ...card, p: 2.5, mb: 2 }}>
        {typeMeta.string ? (
          <TextField fullWidth autoFocus multiline maxRows={type === 'rest' ? 8 : 3} value={connString}
            onChange={(e) => handleConnStringChange(e.target.value)}
            placeholder={type === 'rest'
              ? 'https://api.example.com/v1/endpoint — or paste a cURL command / raw HTTP request'
              : typeMeta.placeholder}
            helperText={type === 'rest'
              ? 'Tip: paste the whole cURL command or HTTP request from the provider\'s docs — we\'ll extract the URL, headers and auth for you.'
              : undefined}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && connString.trim()) { e.preventDefault(); runInspect(); } }}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }} />
        ) : (
          <Alert severity="info" sx={{ mb: 0 }}>Configure the {typeLabel(type)} details in Advanced below, then click Connect.</Alert>
        )}

        {/* REST gets its full panel (auth is essential, not "advanced");
            every other connector keeps the compact Advanced disclosure. */}
        {type === 'rest' ? (
          <RestConnectPanel
            rest={rest} setRest={setRest}
            connString={connString} setConnString={setConnString}
            smartPaste={smartPaste}
            onApplyPreset={applyPreset}
          />
        ) : (
          <Box sx={{ mt: 1.5 }}>
            <Button size="small" onClick={() => setAdvOpen((o) => !o)} startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
              endIcon={<ExpandMoreIcon sx={{ fontSize: 16, transform: advOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
              sx={{ textTransform: 'none', color: 'text.secondary' }}>
              Advanced
            </Button>
            <Collapse in={advOpen}>
              <Box sx={{ pt: 1.5 }}>
                <AdvancedFields type={type} adv={adv} setAdv={setAdv} />
              </Box>
            </Collapse>
          </Box>
        )}

        <Button fullWidth variant="contained" sx={{ mt: 2, py: 1.1, textTransform: 'none' }}
          disabled={(typeMeta.string && !connString.trim()) || inspecting || creating}
          onClick={runInspect}
          startIcon={inspecting || creating ? <CircularProgress size={16} color="inherit" /> : <BoltIcon />}>
          {inspecting ? 'Testing & reading schema…' : creating ? 'Connecting…' : 'Connect'}
        </Button>
      </Paper>
      )}
      </>)}

      {/* No data handy? The onboarding sample catalog, loadable any time. */}
      <Typography sx={{ ...sectionLabel, mt: 1 }}>Or try it without your data</Typography>
      <Paper elevation={0} onClick={() => setSamplesOpen(true)}
        sx={{
          ...card, p: 1.5, mb: 3, display: 'flex', alignItems: 'center', gap: 1.25,
          cursor: 'pointer', transition: '0.12s',
          '&:hover': { borderColor: theme.palette.primary.main, boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}` },
        }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: '8px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main',
        }}>
          <ScienceIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>Load a sample dataset</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>
            Two years of realistic daily data — sales, traffic, markets, weather and more
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'primary.main', flexShrink: 0 }}>
          Browse
        </Typography>
      </Paper>

      <SampleDatasetsDialog open={samplesOpen} onClose={() => setSamplesOpen(false)} />
    </Box>
  );
}

// Per-connector advanced fields (progressive disclosure).
function AdvancedFields({ type, adv, setAdv }) {
  const set = (k) => (e) => setAdv((a) => ({ ...a, [k]: e.target.value }));
  const field = { fullWidth: true, size: 'small', sx: { '& .MuiInputBase-input': { fontSize: 13 } } };

  if (type === 'bigquery') {
    return (
      <Stack spacing={1.25}>
        <TextField {...field} multiline minRows={3} maxRows={6} label="Service account JSON"
          placeholder='{ "type": "service_account", ... }' value={adv.service_account_json || ''} onChange={set('service_account_json')}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }} />
        <TextField {...field} label="API endpoint (optional)" placeholder="https://bigquery.googleapis.com"
          value={adv.endpoint || ''} onChange={set('endpoint')}
          helperText="Only for private Google API endpoints or emulators - leave empty otherwise." />
      </Stack>
    );
  }
  if (type === 's3') {
    return (
      <Stack spacing={1.25}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <TextField {...field} label="Access key ID (private buckets)" value={adv.access_key_id || ''} onChange={set('access_key_id')} />
          <TextField {...field} type="password" label="Secret access key" value={adv.secret_access_key || ''} onChange={set('secret_access_key')} />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <TextField {...field} label="Endpoint URL (S3-compatible stores)" placeholder="https://minio.example.com:9000"
            value={adv.endpoint_url || ''} onChange={set('endpoint_url')} />
          <TextField {...field} label="Region (optional)" placeholder="us-east-1" value={adv.region || ''} onChange={set('region')} />
        </Stack>
      </Stack>
    );
  }
  // REST is handled by RestConnectPanel (full auth/params/headers UX),
  // not this compact disclosure.
  if (type === 'kafka') {
    return (
      <Stack spacing={1.25}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <TextField {...field} select label="Security" value={adv.security_protocol || 'PLAINTEXT'} onChange={set('security_protocol')}>
            {['PLAINTEXT', 'SASL_PLAINTEXT', 'SASL_SSL', 'SSL'].map((p) => <MenuItem key={p} value={p} sx={{ fontSize: 13 }}>{p}</MenuItem>)}
          </TextField>
          <TextField {...field} select label="SASL" value={adv.sasl_mechanism || ''} onChange={set('sasl_mechanism')}>
            {['', 'PLAIN', 'SCRAM-SHA-256', 'SCRAM-SHA-512'].map((m) => <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>{m || 'None'}</MenuItem>)}
          </TextField>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <TextField {...field} label="SASL username (optional)" value={adv.username || ''} onChange={set('username')} />
          <TextField {...field} type="password" label="SASL password (optional)" value={adv.password || ''} onChange={set('password')} />
        </Stack>
      </Stack>
    );
  }
  if (type === 'webhook') {
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
        <TextField {...field} label="Name" value={adv.name || ''} onChange={set('name')} />
        <TextField {...field} type="password" label="Signature secret (optional)" value={adv.signature_secret || ''} onChange={set('signature_secret')} />
      </Stack>
    );
  }
  // database types
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
      <TextField {...field} type="password" label="Password (if not in URL)" value={adv.password || ''} onChange={set('password')} />
      <TextField {...field} label="Schema (optional)" value={adv.schema || ''} onChange={set('schema')} />
      <TextField {...field} label="SSL mode (optional)" placeholder="require" value={adv.sslmode || ''} onChange={set('sslmode')} />
    </Stack>
  );
}
